import { createFileRoute, Link } from "@tanstack/react-router";

import { SiteShell } from "@/components/marketing/site-shell";
import { LegalPage, LegalSection } from "@/components/marketing/legal-page";

const TITLE = "Terms of Service — AROS FLOW";
const DESC =
  "The agreement for using ArosFlow: what the software does, what it does not do, acceptable use, billing and liability limits.";

export const Route = createFileRoute("/legal/terms")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SiteShell>
      <LegalPage title="Terms of Service" summary={DESC}>
        <LegalSection title="1. The agreement">
          <p>
            By creating an ArosFlow account you agree to these terms. ArosFlow is operated by Thy
            Kingdom Come Property &amp; Realty. If you do not agree, do not use the service.
          </p>
        </LegalSection>

        <LegalSection title="2. What ArosFlow is">
          <p>
            ArosFlow is decision-support software for real-estate acquisitions. It organises leads,
            retrieves property data from licensed providers, runs arithmetic on assumptions you can
            see and change, scores seller need from answers you record, and produces reports and
            recommendations.
          </p>
        </LegalSection>

        <LegalSection title="3. What ArosFlow is not">
          <ul>
            <li>
              It is not an appraisal, a broker price opinion, legal advice, tax advice, investment
              advice or a financing commitment.
            </li>
            <li>
              It is not a brokerage. We are not acting as your agent in any transaction, and we
              receive no commission or referral fee from your deals.
            </li>
            <li>
              It is not a lead source and does not promise deal flow, income or any particular
              result. Any figures shown as examples are illustrative only.
            </li>
          </ul>
          <p>
            Every estimate — ARV, repairs, maximum allowable offer, Need Score, confidence, buyer
            match — is an estimate based on the data available and the assumptions you accepted. You
            are responsible for verifying numbers before you make an offer or sign a contract.
          </p>
        </LegalSection>

        <LegalSection title="4. Your account">
          <p>
            You must give a valid email address and keep your password secure. You are responsible
            for everything done under your account. One account is for one person unless you are on
            a plan that explicitly includes multiple users.
          </p>
        </LegalSection>

        <LegalSection title="5. Acceptable use">
          <ul>
            <li>
              Comply with all laws that apply to your outreach, including the TCPA, CAN-SPAM,
              do-not-call rules and your state&rsquo;s real-estate and wholesaling rules.
            </li>
            <li>
              Do not upload contact data you have no lawful basis to hold, and do not use ArosFlow
              to organise unsolicited mass messaging.
            </li>
            <li>Do not resell, scrape or redistribute property data retrieved through ArosFlow.</li>
            <li>Do not attempt to break, overload or reverse-engineer the service.</li>
          </ul>
          <p>
            Compensation you earn must be documented as what it actually is. Do not use ArosFlow
            documents to present brokerage or referral compensation as a service fee.
          </p>
        </LegalSection>

        <LegalSection title="6. Plans, billing and allowances">
          <p>
            Paid plans bill monthly in advance through our payment processor. Live property lookups
            and AI reasoning runs are metered per plan because each one is a paid third-party call;
            manual calculations are not metered on paid plans. Allowances reset monthly and do not
            roll over. We may change prices with at least 30 days&rsquo; notice; the change applies
            from your next billing period.
          </p>
          <p>
            Features marked <strong>early access</strong> are not finished and are not part of what
            you are paying for. We will not bill them as working features.
          </p>
        </LegalSection>

        <LegalSection title="7. Cancellation and refunds">
          <p>
            See the <Link to="/legal/refunds">Refund &amp; Cancellation Policy</Link>.
          </p>
        </LegalSection>

        <LegalSection title="8. Your data">
          <p>
            You own the work you enter. We store and process it to run the service, as described in
            the <Link to="/legal/privacy">Privacy Policy</Link>. You can export it at any time.
          </p>
        </LegalSection>

        <LegalSection title="9. Availability">
          <p>
            We aim for the service to be available continuously but we do not guarantee uptime.
            Third-party data and AI providers can be slow or unavailable; when that happens ArosFlow
            falls back to manual entry rather than inventing data.
          </p>
        </LegalSection>

        <LegalSection title="10. Liability">
          <p>
            To the maximum extent the law allows, our total liability for any claim relating to
            ArosFlow is limited to the amount you paid us in the twelve months before the claim. We
            are not liable for lost profits, lost deals or indirect losses. Nothing here limits
            liability that cannot lawfully be limited.
          </p>
        </LegalSection>

        <LegalSection title="11. Termination">
          <p>
            You may close your account at any time. We may suspend or close an account that breaches
            these terms, and will tell you why where we lawfully can.
          </p>
        </LegalSection>

        <LegalSection title="12. Changes and contact">
          <p>
            We will post material changes on this page and email account holders. Questions:{" "}
            <a href="mailto:support@arosflow.com">support@arosflow.com</a>.
          </p>
        </LegalSection>
      </LegalPage>
    </SiteShell>
  );
}
